import { useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight, 
  TrendingUp, 
  Home, 
  Users, 
  BarChart3,
  Shield,
  Clock,
  Check,
  ChevronRight,
  Phone
} from 'lucide-react';

// Section component with scroll animation
const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ConciergeriePage = () => {
  useSEO({
    title: 'Conciergerie Airbnb à Fougères & Rennes | Confort Hôte',
    description: 'Découvrez la conciergerie Confort Hôte à Fougères, Rennes, Vitré, Dol-de-Bretagne, Pleine-Fougères: gestion Airbnb, accueil voyageurs, ménage et optimisation des locations courte durée.',
    keywords: 'conciergerie Airbnb Fougères, conciergerie Rennes, conciergerie Vitré, conciergerie Dol-de-Bretagne, conciergerie Pleine-Fougères, Confort Hôte',
  });

  const cities = [
    { slug: 'fougeres', name: 'Fougères' },
    { slug: 'rennes', name: 'Rennes' },
    { slug: 'vitre', name: 'Vitré' },
    { slug: 'dol-de-bretagne', name: 'Dol-de-Bretagne' },
    { slug: 'pleine-fougeres', name: 'Pleine-Fougères' },
  ];

  const services = [
    {
      icon: TrendingUp,
      title: "Revenue Management",
      description: "Optimisation permanente de vos tarifs grâce à notre algorithme de pricing dynamique. Nous analysons le marché local, les événements et la saisonnalité pour maximiser votre revenu par nuitée (ADR).",
      features: [
        "Analyse quotidienne du marché",
        "Ajustement automatique des prix",
        "Suivi des événements locaux"
      ]
    },
    {
      icon: Home,
      title: "Pilotage Opérationnel",
      description: "Nous prenons en charge l'intégralité des opérations terrain : ménage professionnel entre chaque séjour, linge hôtelier premium via notre partenaire Quozyli, maintenance préventive et curative.",
      features: [
        "Ménage professionnel",
        "Linge hôtelier premium (Quozyli)",
        "Maintenance et petites réparations"
      ]
    },
    {
      icon: Users,
      title: "Assistance Voyageur 7/7",
      description: "Nos équipes assurent une communication réactive avec vos voyageurs : réponse rapide aux demandes, check-in flexible, résolution des incidents pendant le séjour.",
      features: [
        "Réponse sous 1 heure",
        "Check-in/check-out flexibles",
        "Gestion des urgences"
      ]
    },
    {
      icon: BarChart3,
      title: "Diffusion Multicanal",
      description: "Votre bien est diffusé sur plus de 20 plateformes de réservation (Airbnb, Booking, Abritel, etc.) avec des annonces optimisées et des photos professionnelles.",
      features: [
        "20+ plateformes de diffusion",
        "Annonces optimisées SEO",
        "Photos et descriptions pro"
      ]
    },
    {
      icon: Shield,
      title: "Conformité & Réglementation",
      description: "Nous gérons pour vous les aspects administratifs : déclarations en mairie, collecte et reversement de la taxe de séjour, conformité avec la réglementation locale.",
      features: [
        "Déclaration en mairie",
        "Taxe de séjour gérée",
        "Veille réglementaire"
      ]
    },
    {
      icon: Clock,
      title: "Reporting Transparent",
      description: "Accédez à un tableau de bord mensuel avec tous vos indicateurs : revenus, taux d'occupation, avis voyageurs, comparaison avec le marché.",
      features: [
        "Rapport mensuel détaillé",
        "Indicateurs clés (KPIs)",
        "Comparatif marché local"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="conciergerie-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6"
              >
                Notre offre
              </motion.p>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-medium tracking-tight leading-[1.1] text-[#F5F5F5] mb-8"
              >
                La Conciergerie<br/>
                <span className="text-gold-gradient">tout-en-un</span>
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg md:text-xl text-[#A3A3A3] font-light leading-relaxed mb-8"
              >
                Une offre unique à 18% HT qui couvre l'intégralité du pilotage de votre location courte durée. 
                Pas de frais cachés, pas de mauvaises surprises.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
                  data-testid="cta-audit-conciergerie"
                >
                  <span>Demander un audit gratuit</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="glass-card p-8 md:p-12"
            >
              <div className="text-center mb-8">
                <p className="text-6xl md:text-7xl font-playfair text-[#C5A059] mb-2">18%</p>
                <p className="text-lg text-[#A3A3A3]">Commission HT sur les revenus</p>
              </div>
              <div className="space-y-4">
                {[
                  "Revenue management inclus",
                  "Ménage et blanchisserie",
                  "Assistance voyageur 7/7",
                  "Diffusion multicanal",
                  "Conformité réglementaire",
                  "Reporting mensuel"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-[#F5F5F5]">{item}</span>
                  </div>
                ))}
              </div>
              
              {/* Insurance Notice */}
              <div className="mt-6 pt-6 border-t border-[#262626]">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[#A3A3A3] leading-relaxed">
                    <span className="text-[#F5F5F5] font-medium">Prérequis :</span> Une assurance spécifique à l'activité 
                    de location courte durée est obligatoire. Nous vous accompagnons dans cette démarche.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              Services inclus
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5]">
              Un pilotage complet
            </h2>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 md:p-12 bg-[#121212] border border-[#262626] hover:border-[#C5A059]/20 transition-colors duration-500"
              >
                <div className="lg:col-span-1 flex lg:justify-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 text-[#C5A059]">
                    <service.icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <h3 className="text-2xl font-playfair text-[#F5F5F5] mb-4">{service.title}</h3>
                  <p className="text-[#A3A3A3] font-light leading-relaxed">{service.description}</p>
                </div>
                <div className="lg:col-span-6">
                  <ul className="space-y-3">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full" />
                        <span className="text-[#F5F5F5]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Cities Section */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Zones d'intervention
              </p>
              <h2 className="text-3xl sm:text-4xl font-playfair font-normal tracking-tight text-[#F5F5F5]">
                Découvrez nos expertises locales
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((city) => (
              <Link
                key={city.slug}
                to={`/conciergerie/${city.slug}`}
                className="group relative bg-[#0A0A0A] border border-[#262626] p-8 hover:border-[#C5A059]/30 transition-colors duration-500"
                data-testid={`city-link-${city.slug}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-playfair text-[#F5F5F5] group-hover:text-[#C5A059] transition-colors duration-300">
                    {city.name}
                  </h3>
                  <ChevronRight className="w-5 h-5 text-[#A3A3A3] group-hover:text-[#C5A059] group-hover:translate-x-1 transition-transform duration-300" />
                </div>
                <p className="text-sm text-[#A3A3A3] mt-2">
                  Pilotage Airbnb à {city.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-6">
                Prêt à optimiser<br/>vos revenus locatifs ?
              </h2>
              <p className="text-lg text-[#A3A3A3] font-light leading-relaxed mb-8">
                Obtenez une estimation gratuite du potentiel de votre bien. 
                Notre équipe vous rappelle sous 24h.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
                >
                  <span>Demander un audit gratuit</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:0689224402"
                  className="inline-flex items-center justify-center gap-2 border border-[#C5A059]/30 text-[#C5A059] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-colors duration-300"
                >
                  <Phone className="w-4 h-4" />
                  <span>06 89 22 44 02</span>
                </a>
              </div>
            </div>
            <div className="glass-card p-8 md:p-12">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">+33%</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Revenus moyens</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">18%</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Commission HT</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">20+</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Plateformes</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">7/7</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Assistance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default ConciergeriePage;
